import { DragDropContext, Droppable } from "@hello-pangea/dnd"
import { router } from "@inertiajs/react"
import { useCallback } from "react"

import EmptyTaskState from "@/components/EmptyTaskState"
import TaskItem from "@/components/TaskItem"
import { useDragReorder } from "@/hooks/use-drag-reorder"
import { listListsSortedTasksPath } from "@/routes"
import type { Task } from "@/types"

interface TaskListProps {
  tasks: Task[]
  listId: number
  enableDragDrop?: boolean
  onTaskClick?: (task: Task) => void
  onTaskDelete?: (task: Task) => void
  onAddTask?: () => void
  autoArchiveDays?: number
  status?: "active" | "completed" | "archived"
  selectedIndex?: number
}

export default function TaskList({
  tasks,
  listId,
  enableDragDrop = false,
  onTaskClick,
  onTaskDelete,
  onAddTask,
  autoArchiveDays,
  status = "active",
  selectedIndex = -1,
}: TaskListProps) {
  const handleReordered = useCallback(
    (newTasks: Task[]) => {
      router.post(
        listListsSortedTasksPath(listId),
        { task_ids: newTasks.map((task) => task.id) },
        { preserveScroll: true, preserveState: true, only: [] },
      )
    },
    [listId],
  )

  const { orderedItems: orderedTasks, handleDragEnd } = useDragReorder<Task>({
    items: tasks,
    onReordered: handleReordered,
  })

  if (orderedTasks.length === 0) {
    return <EmptyTaskState status={status} onAddTask={onAddTask} />
  }

  if (!enableDragDrop) {
    return (
      <div
        role="listbox"
        aria-label="Tasks"
        className="divide-border/40 divide-y"
      >
        {orderedTasks.map((task, index) => (
          <TaskItem
            key={task.id}
            task={task}
            listId={listId}
            enableDragDrop={false}
            onTaskClick={onTaskClick}
            onTaskDelete={onTaskDelete}
            autoArchiveDays={autoArchiveDays}
            isSelected={index === selectedIndex}
          />
        ))}
      </div>
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={`task-list-${listId}`}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            role="listbox"
            aria-label="Tasks"
            className="divide-border/40 divide-y"
          >
            {orderedTasks.map((task, index) => (
              <TaskItem
                key={task.id}
                task={task}
                listId={listId}
                enableDragDrop={true}
                onTaskClick={onTaskClick}
                onTaskDelete={onTaskDelete}
                autoArchiveDays={autoArchiveDays}
                dragIndex={index}
                isSelected={index === selectedIndex}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
